import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailPriceGroups, type PriceGroup } from '@/api/retailPriceGroups';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'retail.price_groups.manage';

export default function PriceGroupsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-price-groups', orgId],
    queryFn: () => retailPriceGroups.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<PriceGroup>[]>(
    () => [
      { key: 'code', header: 'Код', width: 150, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      { key: 'sort_order', header: 'Порядок', width: 110, align: 'right', sortValue: (r) => r.sort_order ?? 0, cell: (r) => r.sort_order },
      {
        key: 'is_default',
        header: 'За замовч.',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.is_default ? 1 : 0),
        cell: (r) => (r.is_default ? '✓' : '—'),
      },
      {
        key: 'is_active',
        header: 'Активна',
        width: 110,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати ціновими групами у цій юрособі (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Цінові групи
          <HintBadge
            title="Що таке цінова група?"
            text={
              'Цінова група — це набір роздрібних цін для конкретного контексту продажу ' +
              '(наприклад «Роздріб», «Опт», «Знижковий день», «Маркетплейс Розетка»). ' +
              'Кожна номенклатура може мати різну ціну у різних групах.\n\n' +
              'Як це працює:\n' +
              '• POS-станція має «цінову групу за замовчуванням» — її ціни продаються на касі.\n' +
              '• Для одного товару можна задати: 100 грн у групі «Роздріб» і 85 грн у групі «Опт».\n' +
              '• Документ «Зміна цін» масово оновлює ціни у обраній групі.\n\n' +
              'Якщо у магазині один прайс — створіть одну групу «Роздріб» і позначте її «За замовчуванням».'
            }
          />
        </h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/price-groups/new')}
        >
          + Нова група
        </button>
      </div>
      <DataTable<PriceGroup>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає цінових груп."
        onRowDoubleClick={(r) => navigate(`/retail/price-groups/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="sort_order"
      />
    </>
  );
}
